import React, { useState, useEffect } from 'react';
import { X, Info, Activity, Thermometer, Droplets, LineChart, BarChart2, AreaChart as AreaIcon, ScatterChart, Layout, ChevronLeft, ChevronRight, CheckCircle2, Box, Calendar, Image as ImageIcon, Trash2, Settings2 } from 'lucide-react';
import Chart from 'react-apexcharts';
import api from '../api/axios';

interface NewSensorModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (config: SensorConfig) => void;
}

type SensorType = 'Temperatura' | 'Humidade' | 'Vibração' | 'Outro';
type ChartType = 'Linha' | 'Área' | 'Barras' | 'Scatter';

export interface SensorConfig {
  sensorName: string;
  description: string;
  ifcId: string;
  dataInstalacao: string;
  imageUrl: string;
  imageName: string;
  sensorType: string;
  unit: string;
  supportedChartTypes: ChartType[];
  defaultChartType: ChartType | '';
}

interface FormData {
  sensorName: string;
  description: string;
  ifcId: string;
  dataInstalacao: string;
  imageUrl: string;
  imageName: string;
  sensorType: SensorType | '';
  customType: string;
  unit: string;
  supportedChartTypes: ChartType[];
  defaultChartType: ChartType | '';
}

const NewSensorModal: React.FC<NewSensorModalProps> = ({ projectId, isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    sensorName: '',
    description: '',
    ifcId: '',
    dataInstalacao: '',
    imageUrl: '',
    imageName: '',
    sensorType: '',
    customType: '',
    unit: '',
    supportedChartTypes: [],
    defaultChartType: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setFormData({
        sensorName: '',
        description: '',
        ifcId: '',
        dataInstalacao: '',
        imageUrl: '',
        imageName: '',
        sensorType: '',
        customType: '',
        unit: '',
        supportedChartTypes: [],
        defaultChartType: '',
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateStep = () => {
    const newErrors: { [key: string]: string } = {};
    if (step === 1) {
      if (!formData.sensorName.trim()) newErrors.sensorName = 'Nome do sensor é obrigatório.';
    } else if (step === 2) {
      if (!formData.sensorType) newErrors.sensorType = 'Tipo de sensor é obrigatório.';
      if (formData.sensorType === 'Outro' && !formData.customType.trim()) newErrors.customType = 'Indica o tipo de medição.';
      if (!formData.unit.trim()) newErrors.unit = 'Unidade é obrigatória.';
    } else if (step === 3) {
      if (formData.supportedChartTypes.length === 0) newErrors.supportedChartTypes = 'Seleciona pelo menos um tipo de gráfico.';
      if (!formData.defaultChartType) newErrors.defaultChartType = 'Gráfico por omissão é obrigatório.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const handleSensorTypeChange = (type: SensorType) => {
    let unit = '';
    if (type === 'Temperatura') unit = '°C';
    else if (type === 'Humidade') unit = '%';
    else if (type === 'Vibração') unit = 'mm/s';
    // "Outro": deixa a unidade em branco para o utilizador inserir a métrica.
    setFormData({ ...formData, sensorType: type, unit });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors({ ...errors, image: 'O ficheiro tem de ser uma imagem.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, imageUrl: reader.result as string, imageName: file.name }));
      setErrors(prev => { const { image, ...rest } = prev; return rest; });
    };
    reader.readAsDataURL(file);
  };

  const toggleChartType = (type: ChartType) => {
    const supported = [...formData.supportedChartTypes];
    const index = supported.indexOf(type);
    if (index > -1) {
      supported.splice(index, 1);
      if (formData.defaultChartType === type) {
        setFormData({ ...formData, supportedChartTypes: supported, defaultChartType: supported[0] || '' });
      } else {
        setFormData({ ...formData, supportedChartTypes: supported });
      }
    } else {
      supported.push(type);
      setFormData({ ...formData, supportedChartTypes: supported, defaultChartType: formData.defaultChartType || type });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    const { customType, sensorType, ...rest } = formData;
    // Resolve o tipo efetivo: se for "Outro", usa a métrica personalizada.
    const config: SensorConfig = {
      ...rest,
      sensorType: sensorType === 'Outro' ? customType.trim() : sensorType,
    };
    onSuccess(config);
    onClose();
  };

  if (!isOpen) return null;

  const chartThumbnails: { [key in ChartType]: any } = {
    'Linha': { type: 'line', series: [{ data: [10, 41, 35, 51, 49] }] },
    'Área': { type: 'area', series: [{ data: [10, 41, 35, 51, 49] }] },
    'Barras': { type: 'bar', series: [{ data: [10, 41, 35, 51, 49] }] },
    'Scatter': { type: 'scatter', series: [{ data: [[1, 10], [2, 41], [3, 35], [4, 51], [5, 49]] }] },
  };

  const getChartOptions = (type: string) => ({
    chart: { sparkline: { enabled: true }, animations: { enabled: false } },
    stroke: { curve: 'smooth', width: 2 },
    fill: { opacity: 0.3 },
    colors: ['#3b82f6'],
    plotOptions: { bar: { horizontal: type === 'Barras' } },
  });

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm transition-all animate-in fade-in duration-300">
      <div className="bg-white dark:bg-[#161b26] rounded-[3rem] border border-slate-200 dark:border-slate-800 max-w-2xl w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300 transition-colors">
        
        {/* Progress Header */}
        <div className="p-8 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Novo Sensor</h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
              <X size={20} className="text-slate-400" />
            </button>
          </div>
          
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${step >= s ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-200 dark:border-slate-700 text-slate-300'}`}>
                  {step > s ? <CheckCircle2 size={20} /> : <span className="font-black text-xs">{s}</span>}
                </div>
                {s < 3 && <div className={`w-12 h-0.5 mx-2 ${step > s ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 px-2">
             <span className={`text-[9px] font-black uppercase tracking-widest ${step === 1 ? 'text-blue-600' : 'text-slate-400'}`}>Perfil do Sensor</span>
             <span className={`text-[9px] font-black uppercase tracking-widest ${step === 2 ? 'text-blue-600' : 'text-slate-400'}`}>Unidades e Medidas</span>
             <span className={`text-[9px] font-black uppercase tracking-widest ${step === 3 ? 'text-blue-600' : 'text-slate-400'}`}>Tipos de Gráfico</span>
          </div>
        </div>

        <div className="p-10 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {step === 1 && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Perfil do Sensor</h4>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Informação básica sobre o sensor</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nome do Sensor *</label>
                  <input
                    type="text"
                    value={formData.sensorName}
                    onChange={(e) => setFormData({ ...formData, sensorName: e.target.value })}
                    className={`w-full p-4 bg-slate-50 dark:bg-slate-900 border ${errors.sensorName ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white transition-all`}
                    placeholder="Ex: T1 - Ala Norte"
                  />
                  {errors.sensorName && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.sensorName}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white min-h-[100px] transition-all"
                    placeholder="Breve descrição do sensor e sua localização..."
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Box size={12} /> IFC ID</label>
                    <input
                      type="text"
                      value={formData.ifcId}
                      onChange={(e) => setFormData({ ...formData, ifcId: e.target.value })}
                      className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white transition-all"
                      placeholder="Ex: 2O2Fr$t4X7Zf8NOew3FNld"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Calendar size={12} /> Data de Instalação</label>
                    <input
                      type="date"
                      value={formData.dataInstalacao}
                      onChange={(e) => setFormData({ ...formData, dataInstalacao: e.target.value })}
                      className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white transition-all"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5"><ImageIcon size={12} /> Imagem do Sensor <span className="text-slate-300 dark:text-slate-600">(opcional)</span></label>
                  {formData.imageUrl ? (
                    <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center gap-3 min-w-0">
                        <img src={formData.imageUrl} alt="Sensor" className="w-14 h-14 rounded-xl object-cover border border-slate-200 dark:border-slate-700 shrink-0" />
                        <p className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-tight truncate">{formData.imageName}</p>
                      </div>
                      <button type="button" onClick={() => setFormData({ ...formData, imageUrl: '', imageName: '' })} className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors shrink-0">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 cursor-pointer hover:border-blue-500/40 transition-all">
                      <div className="p-2 bg-blue-600/10 text-blue-600 rounded-lg"><ImageIcon size={16} /></div>
                      <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Selecionar imagem</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                    </label>
                  )}
                  {errors.image && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.image}</p>}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Unidades e Medidas</h4>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Especifique as unidades do sensor</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tipo de Sensor *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {(['Temperatura', 'Humidade', 'Vibração', 'Outro'] as SensorType[]).map((t) => (
                      <button
                        key={t}
                        onClick={() => handleSensorTypeChange(t)}
                        className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${formData.sensorType === t ? 'border-blue-600 bg-blue-50/10 text-blue-600' : 'border-slate-100 dark:border-slate-800 text-slate-400 hover:border-blue-500/30'}`}
                      >
                        {t === 'Temperatura' && <Thermometer size={20} />}
                        {t === 'Humidade' && <Droplets size={20} />}
                        {t === 'Vibração' && <Activity size={20} />}
                        {t === 'Outro' && <Settings2 size={20} />}
                        <span className="text-[9px] font-black uppercase tracking-tight">{t === 'Outro' ? 'Outro Tipo' : t}</span>
                      </button>
                    ))}
                  </div>
                  {errors.sensorType && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.sensorType}</p>}
                </div>
                {formData.sensorType === 'Outro' && (
                  <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tipo de Medição *</label>
                    <input
                      type="text"
                      value={formData.customType}
                      onChange={(e) => setFormData({ ...formData, customType: e.target.value })}
                      className={`w-full p-4 bg-slate-50 dark:bg-slate-900 border ${errors.customType ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white transition-all`}
                      placeholder="Ex: Pressão, Luminosidade, CO₂..."
                    />
                    {errors.customType && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.customType}</p>}
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Unidade *</label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className={`w-full p-4 bg-slate-50 dark:bg-slate-900 border ${errors.unit ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white transition-all`}
                      placeholder={formData.sensorType === 'Outro' ? 'Ex: Pa, lux, ppm...' : 'Ex: °C'}
                    />
                    {errors.unit && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.unit}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Tipos de Gráfico</h4>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Escolha os suportados e o padrão</p>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {(['Linha', 'Área', 'Barras', 'Scatter'] as ChartType[]).map((t) => (
                    <div
                      key={t}
                      onClick={() => toggleChartType(t)}
                      className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer group ${formData.supportedChartTypes.includes(t) ? 'border-blue-600 bg-blue-50/10' : 'border-slate-100 dark:border-slate-800 hover:border-blue-500/30'}`}
                    >
                      <div className="h-16 mb-2 opacity-60 group-hover:opacity-100">
                         <Chart
                           options={getChartOptions(t)}
                           series={chartThumbnails[t].series}
                           type={chartThumbnails[t].type}
                           height="100%"
                         />
                      </div>
                      <div className="flex items-center justify-between">
                         <span className={`text-[9px] font-black uppercase ${formData.supportedChartTypes.includes(t) ? 'text-blue-600' : 'text-slate-400'}`}>{t}</span>
                         <div className={`w-3 h-3 rounded-md border flex items-center justify-center ${formData.supportedChartTypes.includes(t) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>
                           {formData.supportedChartTypes.includes(t) && <CheckCircle2 size={8} />}
                         </div>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.supportedChartTypes && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.supportedChartTypes}</p>}

                {formData.supportedChartTypes.length > 0 && (
                  <div className="space-y-3 bg-slate-50 dark:bg-slate-900/50 p-6 rounded-3xl border border-slate-100 dark:border-slate-800">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Gráfico por Omissão *</label>
                    <div className="flex flex-wrap gap-2">
                       {formData.supportedChartTypes.map(t => (
                         <button
                           key={t}
                           onClick={() => setFormData({...formData, defaultChartType: t})}
                           className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${formData.defaultChartType === t ? 'bg-blue-600 text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700'}`}
                         >
                           {t}
                         </button>
                       ))}
                    </div>
                    {errors.defaultChartType && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.defaultChartType}</p>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="p-8 bg-slate-50 dark:bg-slate-950/30 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4">
           {step > 1 ? (
             <button onClick={handleBack} className="flex items-center gap-2 px-8 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:text-slate-600 transition-all">
               <ChevronLeft size={16} /> Voltar
             </button>
           ) : <div />}
           
           <button
             onClick={step === 3 ? handleSubmit : handleNext}
             disabled={isSubmitting}
             className="flex items-center gap-2 px-10 py-4 bg-blue-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-95 disabled:opacity-50"
           >
             {step === 3 ? (isSubmitting ? 'A Processar...' : 'Concluir') : (
               <>Continuar <ChevronRight size={16} /></>
             )}
           </button>
        </div>
        
        {errors.submit && (
           <div className="px-8 pb-4">
             <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-2xl text-red-600 dark:text-red-400">
                <Activity size={18} />
                <p className="text-[10px] font-black uppercase tracking-widest">{errors.submit}</p>
             </div>
           </div>
        )}
      </div>
    </div>
  );
};

export default NewSensorModal;
